import React, { useState } from 'react';
import { Check, Smartphone, Globe, Cloud, Lock, Zap, Download } from 'lucide-react';
import styles from './Home.module.css';
import { Button } from '../../theme/button';
import { Helmet } from 'react-helmet-async';

const Home = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  return (
    <main className={styles.container}>

    <Helmet>
      <title>Loggable – AI Flight Logbook Digitization App</title>
      <meta
        name="description"
        content="Digitize your paper flight logbook with AI. Convert handwritten logbook pages into secure, exportable digital flight records."
      />
    </Helmet>

      {/* Hero Section with Your Original Styling */}
      <section className={styles.heroSection}>
        <div className={styles.textContent}>
          <h1 className={styles.heading}>
            Digitize Your Paper Flight Logbook with AI
          </h1>
          <p className={styles.paragraph}>
            Loggable is an AI-powered flight logbook app that digitizes handwritten paper logbooks into accurate, searchable digital records. Scan pages, auto-detect entries, and export logs in seconds.
          </p>
          <div className={styles.storeButtons}>
            <a href="https://apps.apple.com/us/app/loggable-paperless-logbook/id6752422564" target="_blank" className={styles.storeLink}>
              <img
                src="assets/downloadSVGs/Appstore.svg"
                alt="Apple App Store Download Button"
                className={styles.storeImage}
              />
            </a>
            <div style={{ position: 'relative' }}>
                <a href="#" className={styles.storeLink} style={{ opacity: 0.6, pointerEvents: 'none' }}>
                  <img
                    src="assets/downloadSVGs/Googleplay.svg"
                    alt="Get it on Google Play - Coming Soon"
                    className={styles.storeImage}
                  />
                </a>
                <span style={{
                  position: 'absolute',
                  top: '100px',
                  right: '25px',
                  background: '#1E9C98',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: 'bold'
                }}>
                  COMING SOON
                </span>
              </div>
          </div>
        </div>
        <div className={styles.imageContent}>
          <img
            src="assets/iPhoneScreenshots/LogPage-portrait.png"
            alt="AI flight logbook app digitizing handwritten paper logbook pages on iPhone"
            className={styles.heroImage}
          />
        </div>
      </section>

      {/* Tablet Showcase Section */}
      <section className={styles.TabletSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.TabletGrid}>
            <div className={styles.TabletImageWrapper}>
              <div className={styles.TabletImageCard}>
                <img
                  src="assets/iPadScreenshots/Edit-portrait.png"
                  alt="iPad Tablet view of AI-powered flight logbook digitization"
                  className={styles.TabletImage}
                />
              </div>
            </div>
            <div className={styles.TabletTextContent}>
              <h2 className={styles.sectionHeading}>
                Optimized for Every Screen
              </h2>
              <p className={styles.sectionSubheading}>
                Experience Loggable's full power on your Tablet. The spacious display lets you view more records at once, making data entry and review faster than ever.
              </p>
              <ul className={styles.featureList}>
                <li className={styles.featureItem}>
                  <Check className={styles.checkIconGreen} />
                  <span className={styles.featureText}>
                    Enhanced layout optimized for larger screens
                  </span>
                </li>
                <li className={styles.featureItem}>
                  <Check className={styles.checkIconGreen} />
                  <span className={styles.featureText}>
                    Effortlessly import and scan handwritten logbook pages directly from your tablet’s camera or photo library
                  </span>
                </li>
                <li className={styles.featureItem}>
                  <Check className={styles.checkIconGreen} />
                  <span className={styles.featureText}>
                    quickly adjust entries and totals with intuitive gestures
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Platform Section */}
      <section className={styles.platformSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionHeadingDark}>
              Works Everywhere You Do
            </h2>
            <p className={styles.sectionSubheadingDark}>
              Seamlessly sync your logbooks across all your devices. Start on your phone, continue on your tablet, review on your desktop.
            </p>
          </div>
          
          <div className={styles.platformGrid}>
            <div className={styles.platformCard + ' ' + styles.platformCardBlue}>
              <div className={styles.platformIconWrapper}>
                <Smartphone className={styles.platformIcon} />
              </div>
              <h3 className={styles.platformCardHeading}>iOS & Android</h3>
              <p className={styles.platformCardText}>Native apps optimized for mobile devices</p>
            </div>
            
            <div className={styles.platformCard + ' ' + styles.platformCardPurple}>
              <div className={styles.platformIconWrapper}>
                <Globe className={styles.platformIcon} />
              </div>
              <h3 className={styles.platformCardHeading}>Web App</h3>
              <p className={styles.platformCardText}>Access from any browser, anywhere</p>
            </div>
            
            <div className={styles.platformCard + ' ' + styles.platformCardGreen}>
              <div className={styles.platformIconWrapper}>
                <Cloud className={styles.platformIcon} />
              </div>
              <h3 className={styles.platformCardHeading}>Cloud Sync</h3>
              <p className={styles.platformCardText}>Automatic backup and synchronization</p>
            </div>
          </div>

          <div className={styles.platformBanner}>
            <div className={styles.platformBannerContent}>
              <div className={styles.platformBannerItem}>
                <Smartphone className={styles.platformBannerIcon} />
                <span className={styles.platformBannerLabel}>iPhone</span>
              </div>
              <div className={styles.platformBannerDivider}>+</div>
              <div className={styles.platformBannerItem}>
                <Smartphone className={styles.platformBannerIcon} />
                <span className={styles.platformBannerLabel}>Tablet</span>
              </div>
              <div className={styles.platformBannerDivider}>+</div>
              <div className={styles.platformBannerItem}>
                <Smartphone className={styles.platformBannerIcon} />
                <span className={styles.platformBannerLabel}>Android</span>
              </div>
              <div className={styles.platformBannerDivider}>+</div>
              <div className={styles.platformBannerItem}>
                <Globe className={styles.platformBannerIcon} />
                <span className={styles.platformBannerLabel}>Web</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={styles.featuresSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionHeadingDark}>
              Powerful Features for Professional Pilots
            </h2>
            <p className={styles.sectionSubheadingDark}>
              Everything you need to maintain accurate, accessible flight records
            </p>
          </div>
          
          <div className={styles.featuresGrid}>
            <div className={styles.featureCard}>
              <Zap className={styles.featureIconYellow} />
              <h3 className={styles.featureCardHeading}>Fast Data Entry</h3>
              <p className={styles.featureCardText}>AI-powered recognition converts handwritten logbook entries into digital records in seconds</p>
            </div>
            
            <div className={styles.featureCard}>
              <Download className={styles.featureIconBlue} />
              <h3 className={styles.featureCardHeading}>Export Anywhere</h3>
              <p className={styles.featureCardText}>Export to Excel (CSV) or PDF for any reporting requirement</p>
            </div>
            
            <div className={styles.featureCard}>
              <Lock className={styles.featureIconGreen} />
              <h3 className={styles.featureCardHeading}>Secure Cloud Sync</h3>
              <p className={styles.featureCardText}>Your logbook data is encrypted, backed up, and accessible across all your devices</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className={styles.pricingSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionHeadingDark}>
              Simple, Transparent Pricing
            </h2>
            <p className={styles.sectionSubheadingDark}>
              Start free, upgrade when you need more
            </p>
          </div>

          <div className={styles.pricingGridTwo}>
            {/* Free Plan */}
            <div className={styles.pricingCard}>
              <div className={styles.pricingHeader}>
                <h3 className={styles.pricingPlanName}>Free</h3>
                <div className={styles.pricingAmount}>$0</div>
                <p className={styles.pricingDescription}>Perfect for trying out Loggable</p>
              </div>
              <ul className={styles.pricingFeatureList}>
                <li className={styles.pricingFeatureItem}>
                  <Check className={styles.checkIconSmall} />
                  <span className={styles.pricingFeatureText}>10 image uploads</span>
                </li>
                <li className={styles.pricingFeatureItem}>
                  <Check className={styles.checkIconSmall} />
                  <span className={styles.pricingFeatureText}>Unlimited exports</span>
                </li>
                <li className={styles.pricingFeatureItem}>
                  <Check className={styles.checkIconSmall} />
                  <span className={styles.pricingFeatureText}>iOS, Android & Web access</span>
                </li>
                <li className={styles.pricingFeatureItem}>
                  <Check className={styles.checkIconSmall} />
                  <span className={styles.pricingFeatureText}>Cloud sync</span>
                </li>
              </ul>
              <Button 
                className={styles.pricingCardButton}
                onClick={() => window.open('https://app.loggable.net', '_blank')}
              >
                Get Started Free
              </Button>
            </div>

            {/* Pro Plan */}
            <div className={styles.pricingCardPro}>
              <div className={styles.popularBadge}>
                MOST POPULAR
              </div>
              <div className={styles.pricingHeader}>
                <h3 className={styles.pricingPlanNameWhite}>Pro</h3>
                <div className={styles.pricingAmountWhite}>
                  $2<span className={styles.pricingPeriod}>/mo</span>
                </div>
                <p className={styles.pricingDescriptionLight}>Billed annually at $24/year</p>
              </div>
              <ul className={styles.pricingFeatureList}>
                <li className={styles.pricingFeatureItem}>
                  <Check className={styles.checkIconYellow} />
                  <span className={styles.pricingFeatureTextWhite}>Unlimited image uploads</span>
                </li>
                <li className={styles.pricingFeatureItem}>
                  <Check className={styles.checkIconYellow} />
                  <span className={styles.pricingFeatureTextWhite}>Unlimited exports</span>
                </li>
                <li className={styles.pricingFeatureItem}>
                  <Check className={styles.checkIconYellow} />
                  <span className={styles.pricingFeatureTextWhite}>iOS, Android & Web access</span>
                </li>
                <li className={styles.pricingFeatureItem}>
                  <Check className={styles.checkIconYellow} />
                  <span className={styles.pricingFeatureTextWhite}>Cloud sync</span>
                </li>
                <li className={styles.pricingFeatureItem}>
                  <Check className={styles.checkIconYellow} />
                  <span className={styles.pricingFeatureTextWhite}>Priority support</span>
                </li>
              </ul>
              <Button 
                className={styles.pricingCardButton}
                onClick={() => window.open('https://app.loggable.net/login?redirect=subscribe', '_blank')}
              >
                Get Pro Access
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaHeading}>
              Ready to Go Digital?
            </h2>
            <p className={styles.ctaSubheading}>
              Join hundreds of pilots who have already made the switch to Loggable
            </p>
            <div className={styles.storeButtons}>
              <a href="https://apps.apple.com/us/app/loggable-paperless-logbook/id6752422564" target="_blank" className={styles.storeLink}>
                <img
                  src="assets/downloadSVGs/Appstore.svg"
                  alt="Download on App Store"
                  className={styles.storeImage}
                />
              </a>
              <div style={{ position: 'relative' }}>
                <a href="#" className={styles.storeLink} style={{ opacity: 0.6, pointerEvents: 'none' }}>
                  <img
                    src="assets/downloadSVGs/Googleplay.svg"
                    alt="Get it on Google Play - Coming Soon"
                    className={styles.storeImage}
                  />
                </a>
                <span style={{
                  position: 'absolute',
                  top: '100px',
                  right: '25px',
                  background: '#000000',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: 'bold'
                }}>
                  COMING SOON
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;